import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Crown, Check, X, Sparkles, Loader2, Calendar, Users, Car, Zap, Cpu } from "lucide-react";
import { api } from "@/lib/api";
import { useEntitlements } from "@/context/EntitlementsContext";
import { toast } from "sonner";

const fmt = (n) => new Intl.NumberFormat("en-IN").format(n || 0);

/**
 * Manage Subscription page (v3 Item 6).
 * Shows current plan, features, limits + comparison of all active plans.
 */
export default function ManageSubscription() {
  const { ent, refresh } = useEntitlements();
  const [billing, setBilling] = useState("monthly");
  const nav = useNavigate();

  useEffect(() => { refresh(); }, [refresh]);

  if (!ent) {
    return (
      <div className="rounded-xl border border-[var(--border-line)] bg-[var(--bg-surface)] p-6 text-sm text-[var(--text-muted)] flex items-center gap-2">
        <Loader2 size={14} className="animate-spin" /> Loading subscription…
      </div>
    );
  }

  const currentPlan = ent.plan || {};
  const sub = ent.subscription || {};
  const usage = ent.usage || {};
  const plans = ent.all_plans || [];
  const isPaid = currentPlan.id && currentPlan.id !== "free";
  const daysLeft = sub.days_remaining;

  const featureKeys = Array.from(
    new Set(plans.flatMap((p) => Object.keys(p.feature_flags || {})))
  ).sort();

  const chooseUpgrade = (planId) => {
    nav(`/subscription/checkout?plan_id=${planId}&billing_cycle=${billing}`);
  };

  return (
    <div id="manage-subscription" className="space-y-6" data-testid="manage-subscription">
      <div>
        <div className="ft-overline">SUBSCRIPTION</div>
        <h2 className="font-display text-3xl font-black tracking-tighter mt-1">Manage subscription</h2>
        <p className="mt-1 text-sm text-[var(--text-muted)]">Your current plan, features and limits. Switch plans below.</p>
      </div>

      {/* Current plan card */}
      <div className="rounded-2xl border border-[#FF5A1F]/40 bg-[#FF5A1F]/5 p-6" data-testid="current-plan-card">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="ft-overline text-[#FF5A1F]">CURRENT PLAN</p>
            <h3 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2">
              {isPaid && <Crown size={18} className="text-[#FFB86B]"/>} {currentPlan.name || currentPlan.id || "Free"}
            </h3>
            <div className="mt-2 flex flex-wrap gap-2 text-xs">
              <Chip label="Status" value={sub.status || "active"} />
              <Chip label="Billing" value={sub.billing_cycle || (isPaid ? "monthly" : "free")} />
              {sub.current_period_start && <Chip icon={Calendar} label="Started" value={new Date(sub.current_period_start).toLocaleDateString("en-IN")} />}
              {sub.current_period_end && <Chip icon={Calendar} label="Renews" value={new Date(sub.current_period_end).toLocaleDateString("en-IN")} />}
              {daysLeft != null && daysLeft >= 0 && (
                <Chip label="Days left" value={`${daysLeft} days`} tone={daysLeft < 30 ? "warn" : "ok"} />
              )}
            </div>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <LimitTile icon={Car}   label="Vehicles" used={usage.vehicles} cap={currentPlan.vehicle_limit} testid="limit-vehicles" />
          <LimitTile icon={Users} label="Members"  used={usage.members}  cap={currentPlan.member_limit}  testid="limit-members" />
          <LimitTile icon={Cpu}   label="AI usage" used={usage.ai_used || 0} cap={currentPlan.ai_ask_monthly_limit ?? currentPlan.ai_limit} testid="limit-ai" />
          <LimitTile icon={Zap}   label="Storage"  used={usage.storage_mb} cap={currentPlan.storage_mb_limit} unit=" MB" testid="limit-storage" />
        </div>
      </div>

      {/* Billing cycle toggle */}
      {plans.some((p) => p.price_monthly > 0) && (
        <div className="flex items-center gap-2 justify-center" data-testid="billing-cycle-toggle">
          <span className={`text-sm ${billing === "monthly" ? "text-white" : "text-[var(--text-muted)]"}`}>Monthly</span>
          <button
            onClick={() => setBilling(billing === "monthly" ? "annual" : "monthly")}
            className={`w-12 h-6 rounded-full border transition-colors ${billing === "annual" ? "bg-[#FF5A1F] border-[#FF5A1F]" : "bg-[var(--bg-surface)] border-[var(--border-line)]"}`}
            data-testid="billing-toggle-btn"
          >
            <span className={`block w-4 h-4 rounded-full bg-white transition-transform ${billing === "annual" ? "translate-x-7" : "translate-x-1"}`} />
          </button>
          <span className={`text-sm ${billing === "annual" ? "text-white" : "text-[var(--text-muted)]"}`}>
            Annual <span className="text-[#22D3EE] text-xs">save up to 33%</span>
          </span>
        </div>
      )}

      {/* Available plans */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="available-plans">
        {plans.length === 0 && (
          <div className="col-span-3 text-sm text-[var(--text-muted)]">No plans configured yet. Ask your Super Admin.</div>
        )}
        {plans.map((p) => {
          const isCurrent = p.id === currentPlan.id;
          const isRecommended = p.recommended || p.id === "pro";
          const monthly = p.price_monthly || 0;
          const annual  = p.price_annual  || (monthly * 12);
          const price   = billing === "monthly" ? monthly : annual;
          const suffix  = billing === "monthly" ? "/mo" : "/yr";
          const savings = billing === "annual" && monthly > 0 && annual > 0
            ? Math.max(0, Math.round(100 - (annual / (monthly * 12)) * 100))
            : 0;
          return (
            <div
              key={p.id}
              data-testid={`plan-${p.id}`}
              className={
                "relative rounded-2xl border p-6 " +
                (isCurrent
                  ? "border-[#FF5A1F] bg-[#FF5A1F]/5"
                  : isRecommended
                  ? "border-[#22D3EE]/60 bg-[#22D3EE]/5"
                  : "border-[var(--border-line)] bg-[var(--bg-surface)]")
              }
            >
              {isRecommended && !isCurrent && (
                <span className="absolute -top-2.5 left-4 text-[10px] px-2 py-0.5 rounded-full bg-[#22D3EE] text-black uppercase tracking-wider">Recommended</span>
              )}
              <div className="flex items-center justify-between">
                <h4 className="font-display text-xl font-black">{p.name || p.id}</h4>
                {isCurrent && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#FF5A1F] text-white uppercase">Current</span>}
              </div>
              <p className="mt-2 text-3xl font-black">
                {price > 0 ? <>₹{fmt(price)}<span className="text-xs font-normal text-[var(--text-muted)]">{suffix}</span></> : "Free"}
              </p>
              {savings > 0 && (
                <p className="text-xs text-[#22D3EE]">Save {savings}% vs monthly</p>
              )}
              {p.trial_days > 0 && !isCurrent && (
                <p className="text-xs text-[var(--text-muted)] mt-1">{p.trial_days}-day free trial</p>
              )}
              <div className="mt-4 text-xs uppercase tracking-wider text-[var(--text-muted)]">Limits</div>
              <ul className="mt-1 space-y-1 text-sm">
                <li>{p.vehicle_limit ?? "—"} vehicles</li>
                <li>{p.member_limit ?? "—"} members</li>
                {p.storage_mb_limit && <li>{fmt(p.storage_mb_limit)} MB storage</li>}
              </ul>
              <div className="mt-3 text-xs uppercase tracking-wider text-[var(--text-muted)]">Features</div>
              <ul className="mt-1 space-y-1 text-sm">
                {featureKeys.map((k) => {
                  const on = !!(p.feature_flags || {})[k];
                  return (
                    <li key={k} className="flex items-center gap-2 text-xs">
                      {on ? <Check size={12} className="text-emerald-400" /> : <X size={12} className="text-[var(--text-muted)]" />}
                      <span className={on ? "" : "text-[var(--text-muted)]"}>{k.replace(/_/g, " ")}</span>
                    </li>
                  );
                })}
              </ul>
              {!isCurrent && (
                <button
                  type="button"
                  onClick={() => chooseUpgrade(p.id)}
                  className="mt-5 w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-[#FF5A1F] hover:bg-[#E64A0F] text-white text-sm font-semibold"
                  data-testid={`upgrade-btn-${p.id}`}
                >
                  <Sparkles size={14} />
                  {price > 0 ? `Choose ${p.name || p.id}` : "Downgrade to Free"}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <p className="text-xs text-[var(--text-muted)]">
        You'll review your order and be redirected to a secure payment page. No card is charged until you confirm.
      </p>
    </div>
  );
}

function Chip({ icon: Icon, label, value, tone = "default" }) {
  const c = tone === "warn" ? "text-yellow-400" : "text-[var(--text-primary)]";
  return (
    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-[var(--border-line)] bg-[var(--bg-surface)] text-[10px]">
      {Icon && <Icon size={10} className="text-[var(--text-muted)]" />}
      <span className="text-[var(--text-muted)] uppercase">{label}:</span>
      <span className={c}>{value}</span>
    </span>
  );
}

function LimitTile({ icon: Icon, label, used, cap, unit = "", testid }) {
  const pct = cap && used != null ? Math.min(100, Math.round((used / cap) * 100)) : null;
  const capLabel = cap == null ? "—" : (cap === Infinity ? "∞" : fmt(cap));
  return (
    <div className="rounded-lg border border-[var(--border-line)] bg-[var(--bg-surface)] p-3" data-testid={testid}>
      <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
        <span>{label}</span>
        <Icon size={12}/>
      </div>
      <div className="mt-1 text-lg font-black">
        {used != null ? fmt(used) : "—"}<span className="text-xs text-[var(--text-muted)]"> / {capLabel}{unit}</span>
      </div>
      {pct != null && (
        <div className="mt-2 h-1 rounded-full bg-[var(--bg-elevated)]">
          <div
            className={"h-full rounded-full " + (pct >= 90 ? "bg-red-500" : pct >= 70 ? "bg-yellow-500" : "bg-[#FF5A1F]")}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
    </div>
  );
}
